import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scroll-reveal for elements with the `.reveal` class (optionally combined
 * with `.reveal-delay-1..4`). Re-runs on route change. Falls back to making
 * everything visible immediately when IntersectionObserver is unavailable or
 * the user prefers reduced motion. Call once, high in the tree (Layout).
 */
export function useReveal(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('is-visible'));
      return;
    }

    let observer: IntersectionObserver | undefined;

    const raf = requestAnimationFrame(() => {
      const targets = document.querySelectorAll('.reveal:not(.is-visible)');
      if (targets.length === 0) return;

      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );

      targets.forEach(el => observer?.observe(el));
    });

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}
